import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ProgramItem } from '@app/app/page-modules/programs/models/program.model';
import { Page } from '@app/app/shared/models/page';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import {
  CohortQuestionDistributionsResponse,
  ProgramSurveyAnalyticsDTO,
  ProgramSurveysListResponse,
  SurveyCourseDetailDTO,
} from '@app/app/shared/services/survey-analytics/survey-analytics.model';
import { ObservableResult } from '@app/app/shared/store';
import { environment } from '@app/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs/operators';
import {
  AdminCoursesRequest,
  CohortGradeData,
  CohortOption,
  ProgramCourseItem,
  ProgramCoursesAnalyticsFilters,
  ProgramCoursesOverview,
  ProgramSkillsOverviewResponse,
  StudentEngagementData,
} from '../../models/courses-analytics.model';
import { CoursesAnalyticsDataService } from './courses-analytics-data.service';

@Injectable()
export class ApiProgramAnalyticsDataService implements CoursesAnalyticsDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  getAdminCourses(requestParams?: AdminCoursesRequest): ObservableResult<Page<ProgramItem>> {
    const params: Record<string, string> = {};
    if (requestParams?.programmeId) {
      params['inProgram'] = requestParams.programmeId;
    }
    if (requestParams?.orgClientId) {
      params['orgClientId'] = requestParams.orgClientId;
    }
    if (requestParams?.page != null) {
      params['page'] = requestParams.page.toString();
    }
    if (requestParams?.size != null) {
      params['size'] = requestParams.size.toString();
    }
    if (requestParams?.term) {
      params['term'] = requestParams.term;
    }
    if (requestParams?.sortBy) {
      params['sortBy'] = requestParams.sortBy;
    }

    return this.client.get<Page<ProgramItem>>(Location.joinWithSlash(environment.apiRootUrl || '', 'analytics/courses/admin'), params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCourses'))),
    );
  }

  getOverview(programId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<ProgramCoursesOverview> {
    const params = this.buildPlaylistParams(filters);
    return this.client
      .get<ProgramCoursesOverview>(
        Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/courses/overview`),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsOverview'))),
      );
  }

  getOverviewByOrgId(
    clientOrgId: string,
    programId: string,
    filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<ProgramCoursesOverview> {
    const params = this.buildPlaylistParams(filters);
    return this.client
      .get<ProgramCoursesOverview>(
        Location.joinWithSlash(environment.apiRootUrl || '', `analytics/clients/${clientOrgId}/programs/${programId}/courses/overview`),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsOverview'))),
      );
  }

  getCourses(programId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<ProgramCourseItem[]> {
    const params: Record<string, string> = {};
    if (filters?.searchQuery) {
      params['search'] = filters.searchQuery;
    }
    if (filters?.statusFilter && filters.statusFilter !== 'all') {
      params['status'] = filters.statusFilter === 'active' ? 'Active' : 'Ended';
    }
    if (filters?.cohort) {
      params['cohort'] = filters.cohort;
    }
    if (filters?.company) {
      params['company'] = filters.company;
    }

    return this.client
      .get<ProgramCourseItem[]>(Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/courses`), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCourses'))),
      );
  }

  getCohortGradeComparison(programId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<CohortGradeData[]> {
    const params: Record<string, string> = {};
    if (filters?.cohortNames?.length) {
      params['cohortNames'] = filters.cohortNames.join(',');
    }
    if (filters?.metric) {
      params['metric'] = filters.metric.toUpperCase();
    }
    if (filters?.dateRange?.start) {
      params['startDate'] = filters.dateRange.start.toISOString();
    }
    if (filters?.dateRange?.end) {
      params['endDate'] = filters.dateRange.end.toISOString();
    }

    return this.client
      .get<
        CohortGradeData[]
      >(Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/courses/cohort-comparison`), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() =>
          ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCohortComparison')),
        ),
      );
  }

  getStudentEngagement(programId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<StudentEngagementData> {
    const params = this.buildPlaylistParams(filters);

    return this.client
      .get<StudentEngagementData>(
        Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/courses/engagement`),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsEngagement'))),
      );
  }

  getCohorts(programId: string): ObservableResult<CohortOption[]> {
    return this.client
      .get<CohortOption[]>(Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/cohorts`))
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCohorts'))),
      );
  }
  getProgramSkillsOverview(programId: string): ObservableResult<ProgramSkillsOverviewResponse> {
    return this.client
      .get<ProgramSkillsOverviewResponse>(Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/skills`))
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsSkills'))),
      );
  }

  getCoursesByOrgId(
    clientOrgId: string,
    programId: string,
    filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<ProgramCourseItem[]> {
    const params: Record<string, string> = {};
    if (filters?.searchQuery) params['search'] = filters.searchQuery;
    if (filters?.statusFilter && filters.statusFilter !== 'all') {
      params['status'] = filters.statusFilter === 'active' ? 'Active' : 'Ended';
    }
    if (filters?.cohort) params['cohort'] = filters.cohort;
    if (filters?.company) params['company'] = filters.company;
    return this.client
      .get<
        ProgramCourseItem[]
      >(Location.joinWithSlash(environment.apiRootUrl || '', `analytics/clients/${clientOrgId}/programs/${programId}/courses`), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCourses'))),
      );
  }

  getCohortGradeComparisonByOrgId(
    clientOrgId: string,
    programId: string,
    filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<CohortGradeData[]> {
    const params: Record<string, string> = {};
    if (filters?.cohortNames?.length) params['cohortNames'] = filters.cohortNames.join(',');
    if (filters?.metric) params['metric'] = filters.metric.toUpperCase();
    if (filters?.dateRange?.start) params['startDate'] = filters.dateRange.start.toISOString();
    if (filters?.dateRange?.end) params['endDate'] = filters.dateRange.end.toISOString();
    return this.client
      .get<
        CohortGradeData[]
      >(Location.joinWithSlash(environment.apiRootUrl || '', `analytics/clients/${clientOrgId}/programs/${programId}/courses/cohort-comparison`), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() =>
          ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCohortComparison')),
        ),
      );
  }

  getStudentEngagementByOrgId(
    clientOrgId: string,
    programId: string,
    filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<StudentEngagementData> {
    return this.client
      .get<StudentEngagementData>(
        Location.joinWithSlash(environment.apiRootUrl || '', `analytics/clients/${clientOrgId}/programs/${programId}/courses/engagement`),
        this.buildPlaylistParams(filters),
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsEngagement'))),
      );
  }

  getCohortsByOrgId(clientOrgId: string, programId: string): ObservableResult<CohortOption[]> {
    return this.client
      .get<
        CohortOption[]
      >(Location.joinWithSlash(environment.apiRootUrl || '', `analytics/clients/${clientOrgId}/programs/${programId}/cohorts`))
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCohorts'))),
      );
  }

  getSurveyAnalyticsByOrgId(
    clientOrgId: string,
    programId: string,
    filters?: { cohortNames?: string[]; metricType?: string; company?: string },
  ): ObservableResult<ProgramSurveyAnalyticsDTO> {
    const params: Record<string, string> = {};
    if (filters?.cohortNames?.length) {
      params['cohortNames'] = filters.cohortNames.join(',');
    }
    if (filters?.metricType) {
      params['metricType'] = filters.metricType;
    }
    if (filters?.company) {
      params['company'] = filters.company;
    }

    return this.client
      .get<ProgramSurveyAnalyticsDTO>(
        Location.joinWithSlash(
          environment.apiRootUrl || '',
          `analytics/clients/${clientOrgId}/programs/${programId}/courses/survey-analytics`,
        ),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.surveyAnalytics.errors.loadSurveys'))),
      );
  }

  getProgramSurveysListByOrgId(clientOrgId: string, programUid: string, client?: string): ObservableResult<ProgramSurveysListResponse> {
    const params: Record<string, string> = {};
    if (client) {
      params['client'] = client;
    }

    return this.client
      .get<ProgramSurveysListResponse>(
        Location.joinWithSlash(environment.apiRootUrl || '', `analytics/clients/${clientOrgId}/programs/${programUid}/courses/surveys`),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() =>
          ObservableResult.ofError(this.translocoService.translate('translations.surveyAnalytics.errors.loadProgramSurveys')),
        ),
      );
  }

  getQuestionDistributionsByOrgId(
    clientOrgId: string,
    programId: string,
    filters?: { cohortNames?: string[]; courseNames?: string[]; company?: string },
  ): ObservableResult<CohortQuestionDistributionsResponse> {
    const params: Record<string, string> = {};
    if (filters?.cohortNames?.length) {
      params['cohortNames'] = filters.cohortNames.join(',');
    }
    if (filters?.courseNames?.length) {
      params['courseNames'] = filters.courseNames.join(',');
    }
    if (filters?.company) {
      params['company'] = filters.company;
    }

    return this.client
      .get<CohortQuestionDistributionsResponse>(
        Location.joinWithSlash(
          environment.apiRootUrl || '',
          `analytics/clients/${clientOrgId}/programs/${programId}/courses/survey-analytics/question-distributions`,
        ),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() =>
          ObservableResult.ofError(this.translocoService.translate('translations.surveyAnalytics.errors.loadSurveyQuestionAnalytics')),
        ),
      );
  }

  getCourseDetailByOrgId(
    clientOrgId: string,
    programId: string,
    courseName: string,
    cohortNames?: string[],
    company?: string,
  ): ObservableResult<SurveyCourseDetailDTO> {
    const params: Record<string, string> = { courseName };
    if (cohortNames?.length) {
      params['cohortNames'] = cohortNames.join(',');
    }
    if (company) {
      params['company'] = company;
    }

    return this.client
      .get<SurveyCourseDetailDTO>(
        Location.joinWithSlash(
          environment.apiRootUrl || '',
          `analytics/clients/${clientOrgId}/programs/${programId}/courses/survey-analytics/course-detail`,
        ),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.surveyAnalytics.errors.loadCourseDetail'))),
      );
  }

  getSurveyAnalytics(
    programId: string,
    filters?: { cohortNames?: string[]; metricType?: string; company?: string },
  ): ObservableResult<ProgramSurveyAnalyticsDTO> {
    const params: Record<string, string> = {};
    if (filters?.cohortNames?.length) {
      params['cohortNames'] = filters.cohortNames.join(',');
    }
    if (filters?.metricType) {
      params['metricType'] = filters.metricType;
    }
    if (filters?.company) {
      params['company'] = filters.company;
    }

    return this.client
      .get<ProgramSurveyAnalyticsDTO>(
        Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/courses/survey-analytics`),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() =>
          ObservableResult.ofError(this.translocoService.translate('translations.surveyAnalytics.errors.loadProgramSurveys')),
        ),
      );
  }

  getSurveyCourseDetail(
    programId: string,
    courseName: string,
    cohortNames?: string[],
    company?: string,
  ): ObservableResult<SurveyCourseDetailDTO> {
    const params: Record<string, string> = { courseName };
    if (cohortNames?.length) {
      params['cohortNames'] = cohortNames.join(',');
    }
    if (company) {
      params['company'] = company;
    }

    return this.client
      .get<SurveyCourseDetailDTO>(
        Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/courses/survey-analytics/course-detail`),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.surveyAnalytics.errors.loadCourseDetail'))),
      );
  }

  getCohortQuestionDistributions(
    programId: string,
    filters?: { cohortNames?: string[]; courseNames?: string[]; company?: string },
  ): ObservableResult<CohortQuestionDistributionsResponse> {
    const params: Record<string, string> = {};
    if (filters?.cohortNames?.length) {
      params['cohortNames'] = filters.cohortNames.join(',');
    }
    if (filters?.courseNames?.length) {
      params['courseNames'] = filters.courseNames.join(',');
    }
    if (filters?.company) {
      params['company'] = filters.company;
    }

    return this.client
      .get<CohortQuestionDistributionsResponse>(
        Location.joinWithSlash(
          environment.apiRootUrl || '',
          `analytics/programs/${programId}/courses/survey-analytics/question-distributions`,
        ),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() =>
          ObservableResult.ofError(this.translocoService.translate('translations.surveyAnalytics.errors.loadSurveyQuestionAnalytics')),
        ),
      );
  }

  private buildPlaylistParams(filters?: ProgramCoursesAnalyticsFilters): Record<string, string> {
    const params: Record<string, string> = {};
    if (filters?.includedPlaylistIds?.length) {
      params['playlistIds'] = filters.includedPlaylistIds.join(',');
    }
    if (filters?.cohort) {
      params['cohort'] = filters.cohort;
    }
    if (filters?.company) {
      params['company'] = filters.company;
    }
    return params;
  }
}
